import api from '../lib/axios';
import { logger } from '../utils/logger';

// API Endpoints from environment variables
const API_ENDPOINTS = {
  CATEGORIES: import.meta.env.VITE_API_V1_CATEGORIES,
  CATEGORIES_PARENT: import.meta.env.VITE_API_V1_CATEGORIES_PARENT,
};

export interface Category {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  icon?: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
  type?: string; // Category type like 'prepaid', 'package', 'eload'
  operatorId?:
    | string
    | {
        // Operator ID for package type categories
        _id: string;
        name: {
          en: string;
          my: string;
        };
        code: string;
      };
  _nameMultilingual?: {
    en: string;
    my: string;
  };
  _descriptionMultilingual?: {
    en: string;
    my: string;
  };
}

export interface CategoryResponse {
  success: boolean;
  message: string;
  data?: Category[];
}

export const getCategoriesWithLanguage = async (language: string = 'en'): Promise<Category[]> => {
  try {
    const response = await api.get<CategoryResponse>(API_ENDPOINTS.CATEGORIES, {
      headers: {
        'Accept-Language': language,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  } catch (error) {
    logger.error('Error fetching categories:', error);
    return [];
  }
};

export const getCategories = async (): Promise<Category[]> => {
  return getCategoriesWithLanguage('en');
};

// Get parent categories with specific parameters
export const getParentCategories = async (language: string = 'en'): Promise<Category[]> => {
  try {
    const response = await api.get<CategoryResponse>('/api/v1/categories/parents', {
      params: {
        isActive: true,
        sortField: 'order',
        sortOrder: 'asc',
      },
      headers: {
        'Accept-Language': language,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  } catch (error) {
    logger.error('Error fetching parent categories:', error);
    return [];
  }
};

// Get parent categories with default English language
export const getParentCategoriesEn = async (): Promise<Category[]> => {
  return getParentCategories('en');
};

// Get subcategories by parent ID
export const getSubcategoriesByParentId = async (
  parentId: string,
  language: string = 'en',
): Promise<Category[]> => {
  try {
    const response = await api.get<CategoryResponse>(
      `${API_ENDPOINTS.CATEGORIES_PARENT}/${parentId}`,
      {
        params: {
          isActive: true,
          sortField: 'order',
          sortOrder: 'asc',
        },
        headers: {
          'Accept-Language': language,
        },
      },
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  } catch (error) {
    logger.error('Error fetching subcategories:', error);
    return [];
  }
};
